import React, { useState } from 'react';
import { Activity, Shield, Pill } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useEncounter } from '@/contexts/EncounterContext';
import { cn } from '@/lib/utils';
import PatientVitalsTab from '../patient/PatientVitalsTab';
import PatientAllergyListTab from '../patient/PatientAllergyListTab';
import PatientMedicationListTab from '../patient/PatientMedicationListTab';

type ContextTab = 'vitals' | 'allergies' | 'medications';

interface PatientContextPanelProps {
  needsReconciliation?: boolean;
  tab?: ContextTab;
  onTabChange?: (tab: ContextTab) => void;
}

const TABS: Array<{ id: ContextTab; label: string; icon: React.ElementType }> = [
  { id: 'vitals', label: 'Vitals', icon: Activity },
  { id: 'allergies', label: 'Allergies', icon: Shield },
  { id: 'medications', label: 'Medications', icon: Pill },
];

/**
 * Phase 4 — combined "Patient context" tab. The right rail surfaces the
 * read-only summary on every screen; this tab is where deeper edits happen
 * (full vitals trend, allergy list with verify status, med-rec).
 */
const PatientContextPanel: React.FC<PatientContextPanelProps> = ({ needsReconciliation, tab, onTabChange }) => {
  const [internalActive, setInternalActive] = useState<ContextTab>('vitals');
  const active = tab ?? internalActive;
  const setActive = (next: ContextTab) => {
    if (onTabChange) onTabChange(next);
    else setInternalActive(next);
  };
  const { patientId } = useEncounter();

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='py-3'>
          <div className='flex flex-wrap items-center gap-2'>
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = active === t.id;
              return (
                <Button
                  key={t.id}
                  type='button'
                  variant={isActive ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => setActive(t.id)}
                  className={cn(
                    'gap-1',
                    isActive && 'bg-brand-teal hover:bg-brand-teal/90',
                  )}
                >
                  <Icon className='h-3.5 w-3.5' />
                  {t.label}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {active === 'vitals' && <PatientVitalsTab patientId={patientId} />}
      {active === 'allergies' && (
        <PatientAllergyListTab
          patientId={patientId}
          needsReconciliation={needsReconciliation}
        />
      )}
      {active === 'medications' && <PatientMedicationListTab patientId={patientId} />}
    </div>
  );
};

export default PatientContextPanel;
